import React from 'react';

import { Box, Button, Grid, Typography } from '@mui/material';

import { Field, Form } from '@hrlogics/react-components/form';

import { CORPORATE_STRUCTURE_OPTIONS, FEIN_MASK, INDUSTRIES_OPTIONS } from '@clearverify/constants';
import { Nullable } from '@clearverify/types';
import { normalizeEmail } from '@clearverify/utils';

import { DefaultDisclaimerInputSection } from 'src/components/disclaimer';
import {
  AddressFields,
  AutocompleteField,
  MaskedTextField,
  NumberField,
  PhoneField,
  SelectField,
  TextField,
} from 'src/components/finalForm';
import { TAX_YEAR_END_DATE_MASK } from 'src/constants/masks';
import { useDemoPrivacyInputSlotProps } from 'src/hooks/demoPrivacyMode';
import { usePeoById } from 'src/hooks/peo';

import { PeoCompanyInformationFormFields, PeoCompanyInformationFormValues } from './types';
import {
  getPeoCompanyInformationInitialValues,
  peoCompanyInformationFormValidationSchema,
} from './utils';

type PeoCompanyInformationFormProps = {
  peoId?: Nullable<string>;
};

export const PeosDetailsCompanyInformationForm: React.FC<PeoCompanyInformationFormProps> = ({
  peoId,
}) => {
  const { peo, updatePeo, isPartnership } = usePeoById(peoId);
  const demoPrivacyInputSlotProps = useDemoPrivacyInputSlotProps();

  const initialValues = React.useMemo(() => getPeoCompanyInformationInitialValues(peo), [peo]);

  const onSubmit = React.useCallback(
    async (formData: PeoCompanyInformationFormValues) => {
      try {
        const headquarters = formData[PeoCompanyInformationFormFields.headquarters];
        await updatePeo({
          displayName: formData.displayName,
          fein: formData.fein,
          industry: formData.industry?.value ?? null,
          corporateStructure: formData.corporateStructure ?? null,
          numberOfEmployees: formData.numberOfEmployees ?? null,
          contactEmail: normalizeEmail(formData.contactEmail),
          contactPhoneNumber: formData.contactPhoneNumber ?? null,
          taxYearEndDate: formData.taxYearEndDate,
          defaultDisclaimer: formData.defaultDisclaimer ?? null,
          headquarters: {
            [peo?.headquarters?.id ? 'update' : 'create']: {
              city: headquarters?.city,
              county: headquarters?.county ?? null,
              state: headquarters?.state,
              street: headquarters?.street,
              zipCode: headquarters?.zipCode,
            },
          },
        });
      } catch (error) {
        console.error(error);
      }
    },
    [peo?.headquarters?.id, updatePeo],
  );

  return (
    <Form<PeoCompanyInformationFormValues>
      onSubmit={onSubmit}
      initialValues={initialValues}
      subscription={{ valid: true, submitting: true, dirty: true }}
      validationSchema={peoCompanyInformationFormValidationSchema}
    >
      {({ submitting, valid, dirty, form }) => {
        return (
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Field
                label="Company Display Name"
                name={PeoCompanyInformationFormFields.displayName}
                fullWidth
                required
                disabled={submitting}
                component={TextField}
                slotProps={demoPrivacyInputSlotProps}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Field
                label="FEIN"
                name={PeoCompanyInformationFormFields.fein}
                fullWidth
                required
                disabled={submitting}
                component={MaskedTextField}
                mask={FEIN_MASK}
                slotProps={demoPrivacyInputSlotProps}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Field
                label="Industry"
                name={PeoCompanyInformationFormFields.industry}
                fullWidth
                disabled={submitting}
                options={INDUSTRIES_OPTIONS}
                component={AutocompleteField}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Field
                label="Corporate Structure"
                name={PeoCompanyInformationFormFields.corporateStructure}
                fullWidth
                disabled={submitting}
                options={CORPORATE_STRUCTURE_OPTIONS}
                component={SelectField}
              />
            </Grid>
            <Grid size={{ xs: 6 }}>
              <Field
                fullWidth
                label="Tax Year End Date"
                name={PeoCompanyInformationFormFields.taxYearEndDate}
                component={MaskedTextField}
                inputProps={{
                  placeholder: 'MM/DD',
                }}
                mask={TAX_YEAR_END_DATE_MASK}
                disabled={submitting}
              />
            </Grid>
            <Grid size={{ xs: 6 }}>
              <Field
                label="Number of Employees"
                name={PeoCompanyInformationFormFields.numberOfEmployees}
                fullWidth
                disabled={submitting}
                component={NumberField}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Field
                label="Primary Contact Phone"
                name={PeoCompanyInformationFormFields.contactPhoneNumber}
                fullWidth
                disabled={submitting}
                component={PhoneField}
                slotProps={demoPrivacyInputSlotProps}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Field
                label="Primary Contact Email"
                name={PeoCompanyInformationFormFields.contactEmail}
                fullWidth
                required
                disabled={submitting}
                component={TextField}
                slotProps={demoPrivacyInputSlotProps}
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Typography variant="subtitle1">Headquarters address</Typography>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <AddressFields
                prefix={PeoCompanyInformationFormFields.headquarters}
                requiredStreet
                requiredCity
                requiredState
                requiredZipCode
                disabled={submitting}
                addressLabel="Street"
                inputSlotProps={demoPrivacyInputSlotProps}
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <DefaultDisclaimerInputSection
                headerText={isPartnership ? `Partnership Disclaimer` : `PEO Disclaimer`}
                name={PeoCompanyInformationFormFields.defaultDisclaimer}
                disabled={submitting}
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Box display="flex" justifyContent="center" gap={2} mt={2}>
                <Button
                  variant="outlined"
                  color="error"
                  onClick={() => form.reset()}
                  disabled={submitting}
                >
                  Reset
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={!valid || submitting || !dirty}
                  loading={submitting}
                >
                  Save changes
                </Button>
              </Box>
            </Grid>
          </Grid>
        );
      }}
    </Form>
  );
};
